import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Container,
} from '@mui/material';
import { FilterAltOutlined } from '@mui/icons-material';
import axios from 'axios';
import API_BASE from '../apiBase';
import GlobalFilters from '../components/GlobalFilters';
import { NoProjectsEmptyState } from '../components/dashboard/EmptyState';
import StatsCards from '../components/StatsCards';
import TrendChart from '../components/TrendChart';
import DetailsTable from '../components/DetailsTable';
import WorkTypeDistribution from '../components/WorkTypeDistribution';
import { JiraAnalysisSkeleton } from '../components/analysis/AnalysisPageSkeleton';
import { Target, CheckCircle, Clock, GitPullRequest } from "lucide-react";



const initialDataState = {
  summary: {},
  trends: [],
  distribution: {},
  epic_analysis: [],
  insights: [],
  ticket_details: [],
};

const JiraAnalysis = () => {
  const [data, setData] = useState(initialDataState);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: '30d',
    customStart: '',
    customEnd: '',
    assignee: 'all',
    jiraType: 'all',
  });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/projects`);
      // Filter projects to only show those with JIRA enabled
      const jiraProjects = (res.data || []).filter(p => p.jira_enabled);
      setProjects(jiraProjects);
      if (jiraProjects.length > 0) {
        setSelectedProject(jiraProjects[0].api_key);
      } else {
        setError('No projects with JIRA enabled found. Please enable JIRA for a project first.');
      }
    } catch (err) {
      setError('Failed to fetch projects.');
      console.error('Project fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Single comprehensive API call to fetch all JIRA analytics data
  const fetchJiraAnalytics = useCallback(async () => {
    if (!selectedProject) {
      // No project: don't fetch and stop loading state
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const analyticsUrl = `${API_BASE}/api/jira/analytics`;
      const params = { 
        project_key: selectedProject, 
        date_range: filters.dateRange 
      };
      
      if (filters.dateRange === 'custom' && filters.customStart && filters.customEnd) {
        params.start_date = filters.customStart;
        params.end_date = filters.customEnd;
      }
      
      const res = await axios.get(analyticsUrl, { params });
      const data = res.data;
      
      // Extract data for different components from the comprehensive response
      setData({
        summary: {
          total_jiras: data.summary.total_tickets || 0,
          avg_cycle_time: data.summary.avg_cycle_time || 0,
          cycle_time_trend: data.summary.cycle_time_trend || 0,
          jira_accuracy: data.summary.jira_accuracy || 0,
          tickets_without_prs: data.summary.tickets_without_prs || 0,
        },
        trends: data.trends || [],
        distribution: data.work_type_distribution || {},
        epic_analysis: data.epic_analysis || [],
        insights: data.insights || [],
        ticket_details: data.ticket_details || [],
      });
      
      console.log('🔍 Debug - Comprehensive JIRA API Response:', data);
      console.log('🔍 Debug - Summary:', data.summary);
      console.log('🔍 Debug - Work Type Distribution:', data.work_type_distribution);
      console.log('🔍 Debug - Ticket Details:', data.ticket_details);
      
    } catch (err) {
      console.error('Failed to fetch JIRA analytics:', err);
      setError('Failed to fetch JIRA analytics data. Please try again.');
      setData(initialDataState);
    } finally {
      setIsLoading(false);
    }
  }, [selectedProject, filters]);

  useEffect(() => {
    fetchJiraAnalytics();
  }, [fetchJiraAnalytics]);

  const { filteredTicketDetails, uniqueAssignees, uniqueTicketTypes } = useMemo(() => {
    const ticketDetails = data.ticket_details || [];
    const filtered = ticketDetails.filter((ticket) => {
      const assigneeMatch = filters.assignee === 'all' || ticket.assignee === filters.assignee;
      const typeMatch = filters.jiraType === 'all' || ticket.jira_id?.startsWith(filters.jiraType);
      return assigneeMatch && typeMatch;
    });

    const assignees = ['all', ...new Set(ticketDetails.map((t) => t.assignee).filter(Boolean))];
    const types = ['all', ...new Set(ticketDetails.map((t) => t.jira_id?.split('-')[0]).filter(Boolean))];

    return {
      filteredTicketDetails: filtered,
      uniqueAssignees: assignees,
      uniqueTicketTypes: types,
    };
  }, [data.ticket_details, filters]);

  // Prepare data for reusable components
  const { epicData, trendData, statsData, workTypeData } = useMemo(() => {
    const summary = data.summary || {};

    // Epic data for WorkTypeDistribution
    const epics = (data.epic_analysis || []).map((e) => ({
      name: e.epic,
      value: e.total_jiras
    }));

    // Trend data for TrendChart - format date like PR analysis (e.g., "Sep 14")
    const trendsRaw = (data.trends || []).map((t) => {
      const d = t.date ? new Date(t.date) : null;
      const isValid = d && !isNaN(d.getTime());
      return {
        label: isValid ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : (t.date || ''),
        average_value: t.avg_cycle_time || 0,
        date: isValid ? d : undefined,
      };
    });

    // Pad trend series to cover full selected date range on X-axis
    const padRange = () => {
      let start = null;
      let end = null;
      const today = new Date();
      const toStartOfDay = (dt) => new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
      const toEndOfDay = (dt) => new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), 23, 59, 59, 999));

      switch (filters.dateRange) {
        case '7d': {
          end = toEndOfDay(today);
          const s = new Date(today);
          s.setUTCDate(s.getUTCDate() - 7);
          start = toStartOfDay(s);
          break;
        }
        case '30d': {
          end = toEndOfDay(today);
          const s = new Date(today);
          s.setUTCDate(s.getUTCDate() - 30);
          start = toStartOfDay(s);
          break;
        }
        case '90d': {
          end = toEndOfDay(today);
          const s = new Date(today);
          s.setUTCDate(s.getUTCDate() - 90);
          start = toStartOfDay(s);
          break;
        }
        case '180d': {
          end = toEndOfDay(today);
          const s = new Date(today);
          s.setUTCDate(s.getUTCDate() - 180);
          start = toStartOfDay(s);
          break;
        }
        case 'custom': {
          if (filters.customStart && filters.customEnd) {
            const s = new Date(filters.customStart);
            const e = new Date(filters.customEnd);
            if (!isNaN(s) && !isNaN(e)) {
              start = toStartOfDay(s);
              end = toEndOfDay(e);
            }
          }
          break;
        }
        default: {
          // For other/overall, do not pad
          return trendsRaw;
        }
      }

      if (!start || !end) return trendsRaw;

      const toUTCKey = (date) => {
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const d = String(date.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      const byDay = new Map();
      trendsRaw.forEach((pt) => {
        if (pt.date) {
          const key = toUTCKey(pt.date);
          byDay.set(key, pt);
        }
      });

      const padded = [];
      for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const key = toUTCKey(d);
        const existing = byDay.get(key);
        if (existing) {
          padded.push(existing);
        } else {
          padded.push({
            label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
            average_value: 0,
            date: new Date(d)
          });
        }
      }
      return padded;
    };

    const trends = padRange();

    // Stats data for StatsCards
    const stats = [
      {
        id: 'total_jiras',
        title: 'Total JIRAs',
        value: summary.total_jiras || 0,
        unit: '',
        subtitle: 'JIRAs created',
        icon: <Target />,
        isTrend: false
      },
      {
        id: 'avg_cycle_time',
        title: 'Avg Cycle Time',
        value: (summary.avg_cycle_time || 0).toFixed(1),
        unit: 'h',
        subtitle: 'Current average',
        icon: <Clock />,
        isTrend: false
      },
      {
        id: 'accuracy',
        title: 'Accuracy',
        value: (summary.jira_accuracy || 0).toFixed(1),
        unit: '%',
        subtitle: 'Estimation accuracy',
        icon: <CheckCircle />,
        isTrend: false
      },
      {
        id: 'cycle_time_trend',
        title: 'Cycle Time Trend',
        value: (summary.cycle_time_trend || 0).toFixed(1),
        unit: '%',
        subtitle: (summary.cycle_time_trend || 0) === 0 ? 'No previous data' : 'vs previous period',
        icon: <GitPullRequest />,
      isTrend: true,
      invertColors: true
      }
    ];

    // Work type distribution data - convert from backend format to frontend format
    const workTypeDistribution = data.distribution || {};
    const workTypes = Object.entries(workTypeDistribution).map(([type, data]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1), // Capitalize first letter
      value: data.count || 0,
      percentage: data.percentage || 0
    }));
    
    return { 
      epicData: epics, 
      trendData: trends, 
      statsData: stats,
      workTypeData: workTypes
    };
  }, [data.epic_analysis, data.trends, data.summary, data.distribution, filters.dateRange, filters.customStart, filters.customEnd]);

  // Table columns configuration
  const tableColumns = useMemo(() => [
    {
      field: 'jira_id',
      headerName: 'JIRA ID',
      minWidth: 120,
      flex: 0.5,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: '#1f1f1f',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'summary',
      headerName: 'Summary',
      minWidth: 200,
      flex: 2,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'cycle_time',
      headerName: 'Cycle Time',
      width: 120,
      minWidth: 120,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || typeof value !== 'number') {
          return (
            <Typography
              variant="body2"
              color="#737373"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              N/A
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              fontWeight: 500
            }}
          >
            {value.toFixed(1)}h
          </Typography>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      minWidth: 100,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            color: params.value === 'Done' ? '#10b981' : '#737373'
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'pr_count',
      headerName: 'PRs',
      width: 80,
      minWidth: 80,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            fontWeight: 500,
            textAlign: 'center'
          }}
        >
          {params.value || 0}
        </Typography>
      )
    },
    { 
      field: 'accuracy', 
      headerName: 'Accuracy', 
      width: 100,
      minWidth: 100,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            fontWeight: 500,
            color: (params.value || 0) >= 80 ? '#10b981' : '#f59e0b'
          }}
        >
          {(params.value || 0).toFixed(1)}%
        </Typography>
      )
    },
    {
      field: 'assignee',
      headerName: 'Assignee',
      minWidth: 120,
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {params.value || 'Unassigned'}
        </Typography>
      )
    },
    {
      field: 'done_at',
      headerName: 'Completed',
      width: 100,
      minWidth: 100,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return (
          <Typography
            variant="body2"
            color="#737373"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            -
          </Typography>
        );
        const date = new Date(value);
        if (isNaN(date.getTime())) return (
          <Typography
            variant="body2"
            color="#737373"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Invalid
          </Typography>
        );

        return (
          <Typography
            variant="body2"
            color="#737373"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </Typography>
        );
      }
    }
  ], []);

  const handleFilterChange = useCallback((name, value) => {
    if (name === 'projectKey') {
      setSelectedProject(value);
      setFilters((prev) => ({ ...prev, projectKey: value }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  if (!selectedProject && !error) {
    return <JiraAnalysisSkeleton />;
  }

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      fontSize: { xs: '0.675rem', sm: '0.875rem' },
      '& fieldset': {
        borderColor: '#e2e8f0',
      },
      '&:hover fieldset': {
        borderColor: '#cbd5e1',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1f1f1f',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: {  xs: '0.675rem', sm: '0.875rem' },
      color: '#737373',
      '&.Mui-focused': {
        color: '#1f1f1f',
      },
    },
  };

  return (
    <Box sx={{
      bgcolor: '#fbfbfb',
      minHeight: '100vh',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
        {/* Header Section */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          {/* Filters Section */}
          {/* Header Section */}
          <Box sx={{
            borderRadius: 1,
            p: { xs: 2, sm: 4 },
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            mb: { xs: 3, md: 4 }
          }}>
            {/* Filters Section */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}>
              <FilterAltOutlined fontSize='12px' sx={{ color: '#374151' }} />
              <Typography
                variant="h6"
                sx={{
                  color: '#374151',
                  fontWeight: 400,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                }}
              >
                Filters:
              </Typography>
              <GlobalFilters
                filters={{ ...filters, projectKey: selectedProject }}
                onFilterChange={handleFilterChange}
                projects={projects}
                showProjectFilter
                showDateRangeFilter
                // showCustomRangeFilter
              />
            </Box>
          </Box>
        </Box>

        {isLoading && <JiraAnalysisSkeleton />}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2
            }}
          >
            {error}
          </Alert>
        )}

        {!isLoading && !error && (
          <>
            {/* Stats Cards */}
            <StatsCards stats={statsData} loading={isLoading} />

            {/* Chart and Work Type Distribution Section */}
            <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mb: { xs: 3, md: 4 } }}>
              <Grid item xs={12} lg={8}>
                <TrendChart
                  title="Cycle Time Trends"
                  data={trendData}
                  loading={isLoading}
                  dataKey="average_value"
                  labelKey="Avg Cycle Time (hrs)"
                  height={{ xs: 300, sm: 350, md: 400 }}
                  valueFormatter={(v) => {
                    if (v === null || v === undefined || typeof v !== 'number') {
                      return '';
                    }
                    return `${v.toFixed(1)}h`;
                  }}
                  emptyMessage="No data available for the selected period."
                  area
                />
              </Grid>

              <Grid item xs={12} lg={4}>
                <WorkTypeDistribution
                  title="Work Type Distribution"
                  data={workTypeData}
                  loading={isLoading}
                />
              </Grid>
            </Grid>

            {/* Table Filters */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 1,
                border: '0 solid #e2e8f0',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                mb: { xs: 3, md: 4 },
                bgcolor: '#ffffff'
              }}
            >
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Assignee</InputLabel>
                    <Select
                      value={filters.assignee}
                      onChange={(e) => handleFilterChange('assignee', e.target.value)}
                      label="Assignee"
                      sx={{ borderRadius: 1, ...textFieldStyles }}
                    >
                      {uniqueAssignees.map((assignee) => (
                        <MenuItem key={assignee} value={assignee}>
                          {assignee === 'all' ? 'All Assignees' : assignee}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>JIRA Type</InputLabel>
                    <Select
                      value={filters.jiraType}
                      onChange={(e) => handleFilterChange('jiraType', e.target.value)}
                      label="JIRA Type"
                      sx={{ borderRadius: 1 }}
                    >
                      {uniqueTicketTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type === 'all' ? 'All Types' : type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* JIRA Details Table */}
            <DetailsTable
              title="JIRA Ticket Details"
              data={filteredTicketDetails}
              columns={tableColumns}
              loading={isLoading}
              height={{ xs: 400, sm: 500, md: 600 }}
              searchEnabled={true}
              searchPlaceholder="Search tickets..."
              searchFields={['jira_id', 'summary', 'assignee']}
              getRowId={(row) => row.jira_id}
              pageSizeOptions={[5, 10, 25]}
              initialPageSize={{ xs: 5, sm: 10 }}
            />
          </>
        )}

        {/* No Projects State */}
        {!isLoading && Array.isArray(projects) && projects.length === 0 && (
          <NoProjectsEmptyState
            onAction={() => (window.location.href = '/projects')}
            actionText="Create Project"
          />
        )}
      </Container>
    </Box>
  );
};

export default JiraAnalysis;